/**
 * Test Facade for Intelligems Integration
 *
 * This module provides a versioned test facade (v1) that exposes Intelligems-related
 * methods for QA Robot Framework integration tests. The facade is only available
 * in test builds and provides a stable API that doesn't couple to internal implementation details.
 * Intelligems is accessed via window.igData object.
 *
 * @version v1
 * @availability test builds only (behind localStorage flag)
 *
 * @usage
 * ```bash
 * # Enable test facade (temporary localStorage approach)
 * # Set in browser console: localStorage.setItem('candyrack-intelligems-test-facade-enabled', 'true')
 * # TODO: APPS-9117 - Will use VITE_TEST_FACADE_ENABLED=true after Vite migration
 * ```
 *
 * @access
 * ```javascript
 * // Available on window.CRTestAdapters.v1 when enabled
 * if (window.CRTestAdapters && window.CRTestAdapters.v1) {
 *   const facade = window.CRTestAdapters.v1;
 *   const price = facade.getIntelligemsVariantPrice(true, '123');
 *   const comparePrice = facade.getIntelligemsComparePriceByVariantId(true, '123');
 *   const isAvailable = facade.isIntelligemsAvailable();
 *   const userId = facade.getIntelligemsUserId('ADD_TO_CART');
 * }
 * ```
 *
 * @security
 * - Only available when localStorage candyrack-intelligems-test-facade-enabled=true
 * - Idempotent - safe to call multiple times from different apps
 * - TODO: APPS-9117 - Will use Vite environment variables after migration
 */

import {
  getIntelligemsVariantPrice,
  getIntelligemsComparePriceByVariantId,
  isIntelligemsAvailable,
  getIntelligemsUserId,
} from '../product-page-placement/intelligemsUtils';
import { REGISTERED_APPS } from '../types/Apps';
import { logInfo } from '../utils/logging';

/**
 * Test Facade v1 Interface
 *
 * Provides stable API for Robot Framework integration tests
 */
export interface IntelligemsTestFacadeV1 {
  /**
   * Get Intelligems variant price in minor units
   * @param isIntelligemsEnabled - Whether Intelligems is enabled
   * @param variantId - The variant ID (can be number or string)
   * @returns Resolved price in minor units or undefined
   */
  getIntelligemsVariantPrice(
    isIntelligemsEnabled: boolean,
    variantId?: number | string,
  ): number | undefined;

  /**
   * Get Intelligems compare-at price by variant ID in minor units
   * @param isIntelligemsEnabled - Whether Intelligems is enabled
   * @param variantId - The variant ID (can be number or string)
   * @returns Compare-at price in minor units, null if explicitly no compare price, undefined if unavailable
   */
  getIntelligemsComparePriceByVariantId(
    isIntelligemsEnabled: boolean,
    variantId?: number | string,
  ): number | null | undefined;

  /**
   * Check if Intelligems is loaded and available
   * @returns true if Intelligems is available, false otherwise
   */
  isIntelligemsAvailable(): boolean;

  /**
   * Get Intelligems user ID if available
   * @param registeredApps - The registered apps context
   * @returns IG user ID if available, null otherwise
   */
  getIntelligemsUserId(registeredApps: REGISTERED_APPS): string | null;
}

/**
 * Test Facade v1 Implementation
 *
 * Wraps internal Intelligems utilities with stable API for testing
 */
const intelligemsTestFacadeV1: IntelligemsTestFacadeV1 = {
  getIntelligemsVariantPrice(
    isIntelligemsEnabled: boolean,
    variantId?: number | string,
  ): number | undefined {
    return getIntelligemsVariantPrice(isIntelligemsEnabled, variantId);
  },

  getIntelligemsComparePriceByVariantId(
    isIntelligemsEnabled: boolean,
    variantId?: number | string,
  ): number | null | undefined {
    return getIntelligemsComparePriceByVariantId(
      isIntelligemsEnabled,
      variantId,
    );
  },

  isIntelligemsAvailable(): boolean {
    return isIntelligemsAvailable();
  },

  getIntelligemsUserId(registeredApps: REGISTERED_APPS): string | null {
    return getIntelligemsUserId(registeredApps);
  },
};

/**
 * Initialize Test Facade
 *
 * Exposes the test facade on window.CRTestAdapters.v1 if enabled
 * Only available when localStorage candyrack-intelligems-test-facade-enabled=true
 * Idempotent - safe to call multiple times
 *
 * @param registeredApp - The registered app context (e.g., REGISTERED_APPS.ADD_TO_CART)
 */
const INTELLIGEMS_TEST_FACADE_LOCALSTORAGE_KEY =
  'candyrack-intelligems-test-facade-enabled';

export function initializeIntelligemsTestFacade(
  registeredApp: REGISTERED_APPS,
): void {
  // Check if test facade is enabled
  // TODO: APPS-9117 - Replace with Vite environment variables (VITE_TEST_FACADE_ENABLED) after migration
  // Currently using localStorage as a temporary solution to avoid webpack DefinePlugin security risks
  const isTestFacadeEnabled =
    localStorage.getItem(INTELLIGEMS_TEST_FACADE_LOCALSTORAGE_KEY) === 'true';

  if (!isTestFacadeEnabled) {
    const storageValue = localStorage.getItem(
      INTELLIGEMS_TEST_FACADE_LOCALSTORAGE_KEY,
    );
    logInfo(
      `Test facade not enabled - localStorage ${INTELLIGEMS_TEST_FACADE_LOCALSTORAGE_KEY}: ${storageValue}`,
      registeredApp,
    );
    return;
  }

  // Check if facade is already initialized
  if (typeof window !== 'undefined' && window.CRTestAdapters?.v1) {
    logInfo('Test facade v1 already initialized, skipping', registeredApp);
    return;
  }

  logInfo('Initializing Intelligems Test Facade v1', registeredApp);

  // Initialize window.CRTestAdapters if it doesn't exist
  if (typeof window !== 'undefined') {
    if (!window.CRTestAdapters) {
      (window as any).CRTestAdapters = {};
    }

    // Expose v1 facade
    (window as any).CRTestAdapters.v1 = intelligemsTestFacadeV1;

    logInfo(
      'Intelligems Test Facade v1 exposed on window.CRTestAdapters.v1',
      registeredApp,
    );
  }
}

export default intelligemsTestFacadeV1;
